import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Card, CircularProgress, Alert, Snackbar, Grid } from '@mui/material';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftButton from 'components/SoftButton';
import SoftInput from 'components/SoftInput';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';

import { createSalesLead } from 'services/salesLeadService';
import { SALES_LEAD_FIELDS, LEAD_TYPE_OPTIONS, requiredFor } from '../fields';

const validationSchema = Yup.object({
  leadType: Yup.string().oneOf(['dealer', 'rep_card']).required(),
  contactName: Yup.string().when('leadType', { is: 'dealer', then: Yup.string().required('Contact name is required') }),
  businessName: Yup.string().when('leadType', { is: 'dealer', then: Yup.string().required('Business name is required') }),
  salesRep: Yup.string().when('leadType', { is: 'rep_card', then: Yup.string().required('Sales rep is required') }),
  salesRepPhone: Yup.string().when('leadType', { is: 'rep_card', then: Yup.string().required('Sales rep phone is required') }),
  businessEmail: Yup.string().email('A valid email must be provided'),
});

const initialValues = {
  leadType: 'dealer',
  ...SALES_LEAD_FIELDS.reduce((acc, f) => ({ ...acc, [f.name]: '' }), {}),
};

function SalesLeadCreate() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      try {
        setSubmitting(true);
        // Drop empty strings so optional fields aren't stored blank.
        const payload = Object.fromEntries(
          Object.entries(values).filter(([, v]) => v !== '' && v !== null)
        );
        await createSalesLead(payload);
        setSnackbar({ open: true, message: 'Sales lead created!', severity: 'success' });
        setTimeout(() => navigate('/sales-leads'), 1200);
      } catch (err) {
        console.error('Error creating sales lead:', err);
        setSnackbar({ open: true, message: 'An unexpected error occurred!', severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <Card>
          <SoftBox p={3}>
            <SoftTypography variant="h6" mb={1}>Create Sales Lead</SoftTypography>
            <SoftTypography variant="body2" color="text" mb={3}>
              A unique personalization link is generated automatically on save.
            </SoftTypography>
            <form onSubmit={formik.handleSubmit}>
              <SoftBox mb={2}>
                <SoftTypography variant="caption" fontWeight="bold">Lead Type *</SoftTypography>
                <select
                  name="leadType"
                  value={formik.values.leadType}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d2d6da', fontSize: '14px' }}
                >
                  {LEAD_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </SoftBox>
              <Grid container spacing={2}>
                {SALES_LEAD_FIELDS.map((f) => (
                  <Grid item xs={12} sm={6} key={f.name}>
                    <SoftTypography variant="caption" fontWeight="bold">
                      {f.label}{requiredFor(f.name, formik.values.leadType) ? ' *' : ''}
                    </SoftTypography>
                    <SoftInput
                      placeholder={f.label}
                      name={f.name}
                      type={f.type || 'text'}
                      value={formik.values[f.name]}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched[f.name] && Boolean(formik.errors[f.name])}
                    />
                    {formik.touched[f.name] && formik.errors[f.name] && (
                      <SoftTypography variant="caption" color="error">{formik.errors[f.name]}</SoftTypography>
                    )}
                  </Grid>
                ))}
              </Grid>
              <SoftBox display="flex" gap={2} mt={3}>
                {!submitting ? (
                  <>
                    <SoftButton type="submit" variant="gradient" color="info" disabled={!formik.isValid}>
                      Create
                    </SoftButton>
                    <SoftButton variant="gradient" color="secondary" onClick={() => navigate('/sales-leads')}>
                      Cancel
                    </SoftButton>
                  </>
                ) : (
                  <CircularProgress size={24} />
                )}
              </SoftBox>
            </form>
          </SoftBox>
        </Card>
      </SoftBox>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Footer />
    </DashboardLayout>
  );
}

export default SalesLeadCreate;
